import { useEffect } from 'react';

import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { applyTenantTheme, clearTenantTheme } from '@/lib/theme';

/**
 * Applies the tenant's brand colours as CSS custom properties on <html>.
 *
 * Runs once system configuration resolves, so the app briefly shows the default
 * palette on a cold load. That is deliberate: blocking first paint on a network
 * round trip costs more than a sub-second colour shift, and the neutral surfaces
 * (which are the bulk of the screen) do not change at all.
 */
export const TenantThemeSetter: React.FC = () => {
    const { data: config } = useSystemConfiguration();

    const primary = config?.theme_primary_color;
    const accent = config?.theme_accent_color;
    const radius = config?.theme_radius;

    useEffect(() => {
        const root = document.documentElement;
        if (!primary && !accent && radius == null) {
            clearTenantTheme();
            delete root.dataset.tenantTheme;
            return;
        }

        applyTenantTheme({ primary, accent, radius });
        // Lets the trader area keep its indigo default only when no tenant
        // colours exist (see `.trader-theme` in index.css).
        root.dataset.tenantTheme = 'true';

        // Keep the PWA/browser chrome colour in step with the brand colour.
        if (primary) {
            const meta = document.querySelector('meta[name="theme-color"]');
            if (meta) meta.setAttribute('content', primary);
        }
    }, [primary, accent, radius]);

    return null;
};

export default TenantThemeSetter;
